import { useEffect, useState } from "react";
import {
  section1Tracker,
  section2Tracker,
  section3Tracker,
  section4Tracker,
  section5Tracker,
  section6Tracker,
  section7Tracker,
  section8Tracker,
  section9Tracker,
  section10Tracker,
  section11Tracker,
} from "./Validation/Utils/SectionErrorTrackers";
import "../../../../css/RecruitmentScreen/ProgressBar.css";
import { ReactComponent as CheckIcon } from "../../../../assets/icons/Circle Check (filled).svg";
import { ReactComponent as ErrorIcon } from "../../../../assets/icons/Circle Error (filled).svg";

// Hardcoded section definitions with direct per-section validation
const SECTIONS = [
  { id: 1, key: "section1", label: "Applicant Details" },
  { id: 2, key: "section2", label: "Professional Qualifications" },
  { id: 3, key: "section3", label: "Work History" },
  { id: 4, key: "section4", label: "Company Directorship Details" },
  { id: 5, key: "section5", label: "Accountant Details" },
  { id: 6, key: "section6", label: "Business Conduct" },
  { id: 7, key: "section7", label: "Fitness and Propriety" },
  { id: 8, key: "section8", label: "Overall Financial Position" },
  { id: 9, key: "section9", label: "Additional Information" },
  { id: 10, key: "section10", label: "Unemployment Details" },
  { id: 11, key: "section11", label: "Declaration" },
];

const ProgressTabs = ({ currentStep, onStepChange }) => {
  // Individual state for each section's error tracking
  const [section1Errors, setSection1Errors] = useState({
    hasErrors: false,
    errorCount: 0,
  });
  const [section2Errors, setSection2Errors] = useState({
    hasErrors: false,
    errorCount: 0,
  });
  const [section3Errors, setSection3Errors] = useState({
    hasErrors: false,
    errorCount: 0,
  });
  const [section4Errors, setSection4Errors] = useState({
    hasErrors: false,
    errorCount: 0,
  });
  const [section5Errors, setSection5Errors] = useState({
    hasErrors: false,
    errorCount: 0,
  });
  const [section6Errors, setSection6Errors] = useState({
    hasErrors: false,
    errorCount: 0,
  });
  const [section7Errors, setSection7Errors] = useState({
    hasErrors: false,
    errorCount: 0,
  });
  const [section8Errors, setSection8Errors] = useState({
    hasErrors: false,
    errorCount: 0,
  });
  const [section9Errors, setSection9Errors] = useState({
    hasErrors: false,
    errorCount: 0,
  });
  const [section10Errors, setSection10Errors] = useState({
    hasErrors: false,
    errorCount: 0,
  });
  const [section11Errors, setSection11Errors] = useState({
    hasErrors: false,
    errorCount: 0,
  });

  const [totalErrors, setTotalErrors] = useState(0);

  // Map state setters to section keys for easier access
  const errorStateMap = {
    section1: section1Errors,
    section2: section2Errors,
    section3: section3Errors,
    section4: section4Errors,
    section5: section5Errors,
    section6: section6Errors,
    section7: section7Errors,
    section8: section8Errors,
    section9: section9Errors,
    section10: section10Errors,
    section11: section11Errors,
  };

  // Setup individual listeners for each section
  useEffect(() => {
    // Map of trackers to their state setters
    const trackerMap = [
      { tracker: section1Tracker, setSectionState: setSection1Errors },
      { tracker: section2Tracker, setSectionState: setSection2Errors },
      { tracker: section3Tracker, setSectionState: setSection3Errors },
      { tracker: section4Tracker, setSectionState: setSection4Errors },
      { tracker: section5Tracker, setSectionState: setSection5Errors },
      { tracker: section6Tracker, setSectionState: setSection6Errors },
      { tracker: section7Tracker, setSectionState: setSection7Errors },
      { tracker: section8Tracker, setSectionState: setSection8Errors },
      { tracker: section9Tracker, setSectionState: setSection9Errors },
      { tracker: section10Tracker, setSectionState: setSection10Errors },
      { tracker: section11Tracker, setSectionState: setSection11Errors },
    ];

    
    const unsubscribers = [];

    // Subscribe to each section's changes using individual trackers
    trackerMap.forEach(({ tracker, setSectionState }) => {
      // Get initial state
      const initialState = tracker.getState();
      // console.log(`🔔 [ProgressBar] ${tracker.sectionKey} initial state:`, {
      //   hasErrors: initialState.hasErrors,
      //   errorCount: initialState.errorCount,
      //   errors: initialState.errors,
      // });
      setSectionState({
        hasErrors: initialState.hasErrors,
        errorCount: initialState.errorCount,
      });

      // Subscribe to updates
      const unsubscribe = tracker.addListener((data) => {
        // console.log(`🔔 [ProgressBar] ${tracker.sectionKey} updated:`, {
        //   hasErrors: data.hasErrors,
        //   errorCount: data.errorCount,
        //   errors: data.errors,
        // });
        setSectionState({
          hasErrors: data.hasErrors,
          errorCount: data.errorCount,
        });
      });

      unsubscribers.push(unsubscribe);
    });

    // Cleanup on unmount
    return () => {
      unsubscribers.forEach((unsub) => {
        if (typeof unsub === "function") unsub();
      });
    };
  }, []);

  // Calculate total errors whenever any section's errors change
  useEffect(() => {
    const total = Object.values(errorStateMap).reduce(
      (sum, state) => sum + (state.errorCount || 0),
      0,
    );
    setTotalErrors(total);

    // Log summary of all section errors in ProgressBar
    const summary = [];
    for (let i = 1; i <= 11; i++) {
      const key = `section${i}`;
      const state = errorStateMap[key];
      summary.push({
        Section: i,
        HasErrors: state.hasErrors ? "❌" : "",
        ErrorCount: state.errorCount,
      });
    }
    // console.log("📊 [ProgressBar] Current Error Summary:");
    // console.table(summary);
  }, [
    section1Errors,
    section2Errors,
    section3Errors,
    section4Errors,
    section5Errors,
    section6Errors,
    section7Errors,
    section8Errors,
    section9Errors,
    section10Errors,
    section11Errors,
  ]);

  // Hardcoded Tab Renderer for Section 1
  const renderTab1 = () => {
    const section = SECTIONS[0];
    const isActive = currentStep === section.id;
    const isCompleted = currentStep > section.id;
    const hasError = section1Errors.hasErrors;

    const status = isActive ? (hasError ? "error" : "complete") : "default";

    const barColorClass = hasError
      ? "progress-tab--bar-error"
      : isCompleted
        ? "progress-tab--bar-complete"
        : "";

    return (
      <button
        key={section.key}
        type="button"
        className={`progress-tab progress-tab--${status} ${barColorClass}`}
        onClick={() => onStepChange(section.id)}
      >
        <div className="progress-tab-left">
          <div className="progress-tab-index">{section.id}.</div>
          <div className="progress-tab-text">
            <div className="progress-tab-title">{section.label}</div>
          </div>
        </div>
        <div className="progress-tab-right">
          {hasError && section1Errors.errorCount > 0 ? (
            <div className="error-count-display">
              {/* <span className="error-count-number">
                {section1Errors.errorCount}
              </span> */}
              <div className="progress-tab-icon error">
                <ErrorIcon />
              </div>
            </div>
          ) : !hasError ? (
            <div className="progress-tab-icon success">
              <CheckIcon />
            </div>
          ) : null}
        </div>
      </button>
    );
  };

  // Hardcoded Tab Renderer for Section 2
  const renderTab2 = () => {
    const section = SECTIONS[1];
    const isActive = currentStep === section.id;
    const isCompleted = currentStep > section.id;
    const hasError = section2Errors.hasErrors;
    const status = isActive ? (hasError ? "error" : "complete") : "default";

    const barColorClass = hasError
      ? "progress-tab--bar-error"
      : isCompleted
        ? "progress-tab--bar-complete"
        : "";

    return (
      <button
        key={section.key}
        type="button"
        className={`progress-tab progress-tab--${status} ${barColorClass}`}
        onClick={() => onStepChange(section.id)}
      >
        <div className="progress-tab-left">
          <div className="progress-tab-index">{section.id}.</div>
          <div className="progress-tab-text">
            <div className="progress-tab-title">{section.label}</div>
          </div>
        </div>
        <div className="progress-tab-right">
          {hasError && section2Errors.errorCount > 0 ? (
            <div className="error-count-display">
              {/* <span className="error-count-number">
                {section2Errors.errorCount}
              </span> */}
              <div className="progress-tab-icon error">
                <ErrorIcon />
              </div>
            </div>
          ) : !hasError ? (
            <div className="progress-tab-icon success">
              <CheckIcon />
            </div>
          ) : null}
        </div>
      </button>
    );
  };

  // Hardcoded Tab Renderer for Section 3
  const renderTab3 = () => {
    const section = SECTIONS[2];
    const isActive = currentStep === section.id;
    const isCompleted = currentStep > section.id;
    const hasError = section3Errors.hasErrors;

    const status = isActive ? (hasError ? "error" : "complete") : "default";

    const barColorClass = hasError
      ? "progress-tab--bar-error"
      : isCompleted
        ? "progress-tab--bar-complete"
        : "";

    return (
      <button
        key={section.key}
        type="button"
        className={`progress-tab progress-tab--${status} ${barColorClass}`}
        onClick={() => onStepChange(section.id)}
      >
        <div className="progress-tab-left">
          <div className="progress-tab-index">{section.id}.</div>
          <div className="progress-tab-text">
            <div className="progress-tab-title">{section.label}</div>
          </div>
        </div>
        <div className="progress-tab-right">
          {hasError && section3Errors.errorCount > 0 ? (
            <div className="error-count-display">
              {/* <span className="error-count-number">
                {section3Errors.errorCount}
              </span> */}
              <div className="progress-tab-icon error">
                <ErrorIcon />
              </div>
            </div>
          ) : !hasError ? (
            <div className="progress-tab-icon success">
              <CheckIcon />
            </div>
          ) : null}
        </div>
      </button>
    );
  };

  // Hardcoded Tab Renderer for Section 4
  const renderTab4 = () => {
    const section = SECTIONS[3];
    const isActive = currentStep === section.id;
    const isCompleted = currentStep > section.id;
    const hasError = section4Errors.hasErrors;

    const status = isActive ? (hasError ? "error" : "complete") : "default";

    const barColorClass = hasError
      ? "progress-tab--bar-error"
      : isCompleted
        ? "progress-tab--bar-complete"
        : "";

    return (
      <button
        key={section.key}
        type="button"
        className={`progress-tab progress-tab--${status} ${barColorClass}`}
        onClick={() => onStepChange(section.id)}
      >
        <div className="progress-tab-left">
          <div className="progress-tab-index">{section.id}.</div>
          <div className="progress-tab-text">
            <div className="progress-tab-title">{section.label}</div>
          </div>
        </div>
        <div className="progress-tab-right">
          {hasError && section4Errors.errorCount > 0 ? (
            <div className="error-count-display">
              {/* <span className="error-count-number">
                {section4Errors.errorCount}
              </span> */}
              <div className="progress-tab-icon error">
                <ErrorIcon />
              </div>
            </div>
          ) : !hasError ? (
            <div className="progress-tab-icon success">
              <CheckIcon />
            </div>
          ) : null}
        </div>
      </button>
    );
  };

  // Hardcoded Tab Renderer for Section 5
  const renderTab5 = () => {
    const section = SECTIONS[4];
    const isActive = currentStep === section.id;
    const isCompleted = currentStep > section.id;
    const hasError = section5Errors.hasErrors;

    const status = isActive ? (hasError ? "error" : "complete") : "default";

    const barColorClass = hasError
      ? "progress-tab--bar-error"
      : isCompleted
        ? "progress-tab--bar-complete"
        : "";

    return (
      <button
        key={section.key}
        type="button"
        className={`progress-tab progress-tab--${status} ${barColorClass}`}
        onClick={() => onStepChange(section.id)}
      >
        <div className="progress-tab-left">
          <div className="progress-tab-index">{section.id}.</div>
          <div className="progress-tab-text">
            <div className="progress-tab-title">{section.label}</div>
          </div>
        </div>
        <div className="progress-tab-right">
          {hasError && section5Errors.errorCount > 0 ? (
            <div className="error-count-display">
              {/* <span className="error-count-number">
                {section5Errors.errorCount}
              </span> */}
              <div className="progress-tab-icon error">
                <ErrorIcon />
              </div>
            </div>
          ) : !hasError ? (
            <div className="progress-tab-icon success">
              <CheckIcon />
            </div>
          ) : null}
        </div>
      </button>
    );
  };

  // Hardcoded Tab Renderer for Section 6
  const renderTab6 = () => {
    const section = SECTIONS[5];
    const isActive = currentStep === section.id;
    const isCompleted = currentStep > section.id;
    const hasError = section6Errors.hasErrors;

    const status = isActive ? (hasError ? "error" : "complete") : "default";

    const barColorClass = hasError
      ? "progress-tab--bar-error"
      : isCompleted
        ? "progress-tab--bar-complete"
        : "";

    return (
      <button
        key={section.key}
        type="button"
        className={`progress-tab progress-tab--${status} ${barColorClass}`}
        onClick={() => onStepChange(section.id)}
      >
        <div className="progress-tab-left">
          <div className="progress-tab-index">{section.id}.</div>
          <div className="progress-tab-text">
            <div className="progress-tab-title">{section.label}</div>
          </div>
        </div>
        <div className="progress-tab-right">
          {hasError && section6Errors.errorCount > 0 ? (
            <div className="error-count-display">
              {/* <span className="error-count-number">
                {section6Errors.errorCount}
              </span> */}
              <div className="progress-tab-icon error">
                <ErrorIcon />
              </div>
            </div>
          ) : !hasError ? (
            <div className="progress-tab-icon success">
              <CheckIcon />
            </div>
          ) : null}
        </div>
      </button>
    );
  };

  // Hardcoded Tab Renderer for Section 7
  const renderTab7 = () => {
    const section = SECTIONS[6];
    const isActive = currentStep === section.id;
    const isCompleted = currentStep > section.id;
    const hasError = section7Errors.hasErrors;

    const status = isActive ? (hasError ? "error" : "complete") : "default";

    const barColorClass = hasError
      ? "progress-tab--bar-error"
      : isCompleted
        ? "progress-tab--bar-complete"
        : "";

    return (
      <button
        key={section.key}
        type="button"
        className={`progress-tab progress-tab--${status} ${barColorClass}`}
        onClick={() => onStepChange(section.id)}
      >
        <div className="progress-tab-left">
          <div className="progress-tab-index">{section.id}.</div>
          <div className="progress-tab-text">
            <div className="progress-tab-title">{section.label}</div>
          </div>
        </div>
        <div className="progress-tab-right">
          {hasError && section7Errors.errorCount > 0 ? (
            <div className="error-count-display">
              {/* <span className="error-count-number">
                {section7Errors.errorCount}
              </span> */}
              <div className="progress-tab-icon error">
                <ErrorIcon />
              </div>
            </div>
          ) : !hasError ? (
            <div className="progress-tab-icon success">
              <CheckIcon />
            </div>
          ) : null}
        </div>
      </button>
    );
  };

  // Hardcoded Tab Renderer for Section 8
  const renderTab8 = () => {
    const section = SECTIONS[7];
    const isActive = currentStep === section.id;
    const isCompleted = currentStep > section.id;
    const hasError = section8Errors.hasErrors;

    const status = isActive ? (hasError ? "error" : "complete") : "default";

    const barColorClass = hasError
      ? "progress-tab--bar-error"
      : isCompleted
        ? "progress-tab--bar-complete"
        : "";

    return (
      <button
        key={section.key}
        type="button"
        className={`progress-tab progress-tab--${status} ${barColorClass}`}
        onClick={() => onStepChange(section.id)}
      >
        <div className="progress-tab-left">
          <div className="progress-tab-index">{section.id}.</div>
          <div className="progress-tab-text">
            <div className="progress-tab-title">{section.label}</div>
          </div>
        </div>
        <div className="progress-tab-right">
          {hasError && section8Errors.errorCount > 0 ? (
            <div className="error-count-display">
              {/* <span className="error-count-number">
                {section8Errors.errorCount}
              </span> */}
              <div className="progress-tab-icon error">
                <ErrorIcon />
              </div>
            </div>
          ) : !hasError ? (
            <div className="progress-tab-icon success">
              <CheckIcon />
            </div>
          ) : null}
        </div>
      </button>
    );
  };

  // Hardcoded Tab Renderer for Section 9
  const renderTab9 = () => {
    const section = SECTIONS[8];
    const isActive = currentStep === section.id;
    const isCompleted = currentStep > section.id;
    const hasError = section9Errors.hasErrors;

    const status = isActive ? (hasError ? "error" : "complete") : "default";

    const barColorClass = hasError
      ? "progress-tab--bar-error"
      : isCompleted
        ? "progress-tab--bar-complete"
        : "";

    return (
      <button
        key={section.key}
        type="button"
        className={`progress-tab progress-tab--${status} ${barColorClass}`}
        onClick={() => onStepChange(section.id)}
      >
        <div className="progress-tab-left">
          <div className="progress-tab-index">{section.id}.</div>
          <div className="progress-tab-text">
            <div className="progress-tab-title">{section.label}</div>
          </div>
        </div>
        <div className="progress-tab-right">
          {hasError && section9Errors.errorCount > 0 ? (
            <div className="error-count-display">
              {/* <span className="error-count-number">
                {section9Errors.errorCount}
              </span> */}
              <div className="progress-tab-icon error">
                <ErrorIcon />
              </div>
            </div>
          ) : !hasError ? (
            <div className="progress-tab-icon success">
              <CheckIcon />
            </div>
          ) : null}
        </div>
      </button>
    );
  };

  // Hardcoded Tab Renderer for Section 10
  const renderTab10 = () => {
    const section = SECTIONS[9];
    const isActive = currentStep === section.id;
    const isCompleted = currentStep > section.id;
    const hasError = section10Errors.hasErrors;

    const status = isActive ? (hasError ? "error" : "complete") : "default";

    const barColorClass = hasError
      ? "progress-tab--bar-error"
      : isCompleted
        ? "progress-tab--bar-complete"
        : "";

    return (
      <button
        key={section.key}
        type="button"
        className={`progress-tab progress-tab--${status} ${barColorClass}`}
        onClick={() => onStepChange(section.id)}
      >
        <div className="progress-tab-left">
          <div className="progress-tab-index">{section.id}.</div>
          <div className="progress-tab-text">
            <div className="progress-tab-title">{section.label}</div>
          </div>
        </div>
        <div className="progress-tab-right">
          {hasError && section10Errors.errorCount > 0 ? (
            <div className="error-count-display">
              {/* <span className="error-count-number">
                {section10Errors.errorCount}
              </span> */}
              <div className="progress-tab-icon error">
                <ErrorIcon />
              </div>
            </div>
          ) : !hasError ? (
            <div className="progress-tab-icon success">
              <CheckIcon />
            </div>
          ) : null}
        </div>
      </button>
    );
  };

  // Hardcoded Tab Renderer for Section 11
  const renderTab11 = () => {
    const section = SECTIONS[10];
    const isActive = currentStep === section.id;
    const isCompleted = currentStep > section.id;
    const hasError = section11Errors.hasErrors;

    const status = isActive ? (hasError ? "error" : "complete") : "default";

    const barColorClass = hasError
      ? "progress-tab--bar-error"
      : isCompleted
        ? "progress-tab--bar-complete"
        : "";

    return (
      <button
        key={section.key}
        type="button"
        className={`progress-tab progress-tab--${status} ${barColorClass}`}
        onClick={() => onStepChange(section.id)}
      >
        <div className="progress-tab-left">
          <div className="progress-tab-index">{section.id}.</div>
          <div className="progress-tab-text">
            <div className="progress-tab-title">{section.label}</div>
          </div>
        </div>
        <div className="progress-tab-right">
          {hasError && section11Errors.errorCount > 0 ? (
            <div className="error-count-display">
              {/* <span className="error-count-number">
                {section11Errors.errorCount}
              </span> */}
              <div className="progress-tab-icon error">
                <ErrorIcon />
              </div>
            </div>
          ) : !hasError ? (
            <div className="progress-tab-icon success">
              <CheckIcon />
            </div>
          ) : null}
        </div>
      </button>
    );
  };

  // Hardcoded Tab Renderer for Section 12 (Review and Submit - no validation)

  return (
    <aside className="progress-tabs">
      {/* Error Summary Banner */}
      {/* {totalErrors > 0 && (
        <div className="progress-error-summary" role="alert" aria-live="polite">
          <div className="error-summary-content">
            <span className="error-count-badge">{totalErrors}</span>
            <span className="error-summary-text">
              {totalErrors} error{totalErrors !== 1 ? "s" : ""} across form
            </span>
          </div>
        </div>
      )} */}

      <nav className="progress-tabs-list" aria-label="Application sections">
        {renderTab1()}
        {renderTab2()}
        {renderTab3()}
        {renderTab4()}
        {renderTab5()}
        {renderTab6()}
        {renderTab7()}
        {renderTab8()}
        {renderTab9()}
        {renderTab10()}
        {renderTab11()}
      </nav>
    </aside>
  );
};

export default ProgressTabs;
